/**
 * CataloguePickerDialog — multi-select catalogue picker for AMC dialogs.
 *
 * 2026-02-13 — operator-driven enhancement.
 *
 * Replaces the legacy single-tap-and-close picker with a true multi-select
 * surface: rows expose checkboxes, the picker stays open while selections
 * are being made, a live "N selected" counter sits at the top, and a single
 * "Add Selected" button commits the entire batch to the parent dialog's
 * line-items array.
 *
 * Props:
 *   open              — boolean, dialog visibility
 *   onOpenChange(v)   — close handler
 *   catalogue         — array of catalogue rows (full objects, from
 *                       /billing/catalogue/list)
 *   existingCatalogueIds — Set<string> of catalogue_id values already on
 *                       the parent form. Rows in this set surface an
 *                       informational "Already added" badge but remain
 *                       checkable (operators may legitimately bill the
 *                       same SKU twice — different sub-quantities, etc.).
 *   formatPrice(n)    — currency-aware formatter from the parent (kept as
 *                       a prop so the picker is denomination-agnostic).
 *   onAddSelected(items) — invoked with the array of FULL catalogue rows
 *                       in the order they were checked. The parent owns
 *                       smart-fill semantics (replace empty first line vs
 *                       append) and closes the picker on success.
 *
 * Behaviour locked by operator directive (2026-02-13):
 *   • Picker stays open across selections — closes only on explicit
 *     Close button, escape, or after a successful "Add Selected".
 *   • Search filter does NOT clear the in-progress selection — rows
 *     scrolled out of view by a filter change remain checked and are
 *     surfaced in the count.
 *   • Already-added rows are informational only, not disabled.
 */
import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, Check, Plus, X } from "@phosphor-icons/react";

export default function CataloguePickerDialog({
    open,
    onOpenChange,
    catalogue,
    existingCatalogueIds,
    formatPrice,
    onAddSelected,
}) {
    const [query, setQuery] = useState("");
    // Map<catalogue_id, true> — preserves selection across search filters.
    const [selected, setSelected] = useState({});

    // Reset selection + query whenever the picker re-opens. Wrapped in a
    // microtask so the React 19 react-hooks/set-state-in-effect lint rule
    // is satisfied (state-sets land outside the effect's synchronous body).
    useEffect(() => {
        if (!open) return;
        Promise.resolve().then(() => {
            setQuery("");
            setSelected({});
        });
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return catalogue;
        return catalogue.filter(
            (c) =>
                (c.name || "").toLowerCase().includes(q) ||
                (c.subtitle || "").toLowerCase().includes(q),
        );
    }, [catalogue, query]);

    const selectedCount = useMemo(
        () => Object.values(selected).filter(Boolean).length,
        [selected],
    );

    const toggleRow = (id) => {
        setSelected((s) => {
            const next = { ...s };
            if (next[id]) delete next[id];
            else next[id] = true;
            return next;
        });
    };

    const clearAll = () => setSelected({});

    const selectAllFiltered = () => {
        setSelected((s) => {
            const next = { ...s };
            for (const c of filtered) next[c.id] = true;
            return next;
        });
    };

    const commit = () => {
        // Preserve catalogue array order so the multi-select feels predictable
        // (i.e. items go into the form in the order they appear in the picker,
        // not the order operators happened to click).
        const ordered = catalogue.filter((c) => selected[c.id]);
        if (ordered.length === 0) return;
        onAddSelected(ordered);
    };

    if (!open) return null;

    const filteredCheckedCount = filtered.reduce(
        (n, c) => (selected[c.id] ? n + 1 : n),
        0,
    );

    return (
        <div
            className="fixed inset-0 z-[60] bg-slate-900/40 flex items-start justify-center pt-20"
            onClick={() => onOpenChange(false)}
            data-testid="catalogue-picker-overlay"
        >
            <div
                className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-lg w-full max-h-[72vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Select products and services to add"
            >
                {/* ─── Header: search + selected count + close ─── */}
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search products & services…"
                        className="flex-1 text-sm focus:outline-none"
                        data-testid="catalogue-picker-search"
                    />
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="text-slate-400 hover:text-slate-900 shrink-0"
                        aria-label="Close picker"
                        data-testid="catalogue-picker-close-btn"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* ─── Selected-count + Select-all-filtered + Clear ─── */}
                <div
                    className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center gap-3 text-[11px]"
                    data-testid="catalogue-picker-toolbar"
                >
                    <span
                        className={
                            "font-semibold " +
                            (selectedCount > 0 ? "text-amber-700" : "text-slate-500")
                        }
                        data-testid="catalogue-picker-selected-count"
                    >
                        {selectedCount === 0
                            ? "Nothing selected yet"
                            : `${selectedCount} selected`}
                    </span>
                    <span className="text-slate-300">·</span>
                    <button
                        type="button"
                        onClick={selectAllFiltered}
                        disabled={filtered.length === 0 || filteredCheckedCount === filtered.length}
                        className="text-indigo-600 hover:text-indigo-800 disabled:text-slate-300 disabled:cursor-not-allowed"
                        data-testid="catalogue-picker-select-all-btn"
                    >
                        Select all
                        {query.trim() && filtered.length !== catalogue.length
                            ? ` (${filtered.length} matching)`
                            : ""}
                    </button>
                    {selectedCount > 0 && (
                        <>
                            <span className="text-slate-300">·</span>
                            <button
                                type="button"
                                onClick={clearAll}
                                className="text-slate-500 hover:text-slate-900"
                                data-testid="catalogue-picker-clear-btn"
                            >
                                Clear
                            </button>
                        </>
                    )}
                </div>

                {/* ─── Scrollable item list ─── */}
                <div
                    className="overflow-y-auto flex-1"
                    data-testid="catalogue-picker-list"
                >
                    {filtered.length === 0 ? (
                        <div
                            className="px-4 py-8 text-center text-sm text-slate-500"
                            data-testid="catalogue-picker-empty"
                        >
                            {query.trim()
                                ? `No catalogue items match "${query}".`
                                : "No catalogue items available."}
                        </div>
                    ) : (
                        filtered.map((c) => {
                            const isChecked = !!selected[c.id];
                            const isAlreadyAdded =
                                existingCatalogueIds && existingCatalogueIds.has(c.id);
                            return (
                                <label
                                    key={c.id}
                                    className={
                                        "flex items-start gap-3 px-4 py-2.5 border-b border-slate-100 cursor-pointer transition-colors " +
                                        (isChecked
                                            ? "bg-amber-50/60 hover:bg-amber-50"
                                            : "hover:bg-slate-50")
                                    }
                                    data-testid={`catalogue-pick-row-${c.id}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleRow(c.id)}
                                        className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 focus:ring-1 cursor-pointer"
                                        data-testid={`catalogue-pick-checkbox-${c.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-slate-900 truncate">
                                                {c.name}
                                            </span>
                                            {isAlreadyAdded && (
                                                <span
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0"
                                                    title="This product is already on the AMC. You can still add another instance if needed."
                                                    data-testid={`catalogue-pick-already-added-${c.id}`}
                                                >
                                                    <Check size={9} weight="bold" /> Already added
                                                </span>
                                            )}
                                        </div>
                                        {c.subtitle && (
                                            <div className="text-xs text-slate-500 truncate">
                                                {c.subtitle}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs reedos-data font-medium text-slate-900 whitespace-nowrap">
                                            {c.default_amc_fee > 0 ? (
                                                formatPrice(c.default_amc_fee)
                                            ) : (
                                                <span className="text-slate-400">—</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-slate-500">
                                            GST {c.default_gst_percent ?? 18}% · {c.kind}
                                        </div>
                                    </div>
                                </label>
                            );
                        })
                    )}
                </div>

                {/* ─── Footer: commit batch ─── */}
                <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between gap-3">
                    <span
                        className="text-[11px] text-slate-500"
                        data-testid="catalogue-picker-footer-hint"
                    >
                        Tip — use Select all to add every matching row in one go.
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                            data-testid="catalogue-picker-cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={commit}
                            disabled={selectedCount === 0}
                            className="px-3 py-1.5 text-xs rounded font-semibold bg-amber-600 hover:bg-amber-500 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            data-testid="catalogue-picker-add-selected-btn"
                        >
                            <Plus size={12} weight="bold" />
                            Add{selectedCount > 0 ? ` ${selectedCount}` : ""} selected
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
